import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartModule } from 'primeng/chart';
import { SearchBoxComponent } from '../../../shared/components/search-box/search-box.component';
import { DatePickerModule } from 'primeng/datepicker';
import { ITransaction, ITransactionStats, ITransactionFilters, ITransactionUserInfo, IStatCard } from '../../../Utils/_models/payment-transactions.model';
import { TransactionDetailsModalComponent } from './transaction-details-modal/transaction-details-modal.component';
import { ConfirmService } from '../../../Utils/_services/confirmation-dialog.service';
import { forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

const AVATAR_PALETTES = [
  { bg: '#fee2e2', color: '#ef4444' },
  { bg: '#ede9fe', color: '#7c3aed' },
  { bg: '#dcfce7', color: '#16a34a' },
  { bg: '#fef3c7', color: '#f59e0b' },
  { bg: '#dbeafe', color: '#1d4ed8' },
  { bg: '#fce7f3', color: '#ec4899' },
];

import { CountUpDirective } from '../../../Utils/_directives/count-up.directive';
import { PaymentTransactionsService } from '../../../Utils/_services/admin/payment/payment.service';

@Component({
  selector: 'app-payment-transactions',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartModule, TransactionDetailsModalComponent, SearchBoxComponent, DatePickerModule, CountUpDirective],
  templateUrl: './payment-transactions.component.html',
  styleUrl: './payment-transactions.component.css'
})
export class PaymentTransactionsComponent implements OnInit {

  @ViewChild('statsScroll') statsScroll!: ElementRef<HTMLDivElement>;

  statCards:           IStatCard[]       = [];
  transactions: ITransaction[] = [];
  revenueChartData:    any              = null;
  revenueChartOptions: any;

  periods      = this.svc.getPeriods();
  activePeriod = this.periods[1]; // 30 Days default

  totalCount = 0;
  page       = 1;
  limit      = 5;
  hasMore    = false;
  pageSizes  = [5, 10, 20, 50];

  currentSortField: string | null = null;
  currentSortOrder: 'asc' | 'desc' | null = null;
  originalTransactions: ITransaction[] = [];

  showDetailsModal = false;
  selectedTransaction: ITransaction | null = null;

  viewTransaction(t: ITransaction): void {
    this.selectedTransaction = t;
    this.showDetailsModal = true;
  }

  closeDetailsModal(): void {
    this.selectedTransaction = null;
    this.showDetailsModal = false;
  }

  onPageSizeChange(newSize: any) {

    this.limit = Number(newSize);
    this.page = 1;
    this.loadTransactions();
  }

  // ── Selection State ───────────────────────────────
  allSelected = false;

  toggleSelectAll(): void {
    this.transactions.forEach((t: any) => (t.selected = this.allSelected));
  }

  onTransactionSelect(): void {
    this.allSelected = this.transactions.length > 0 && this.transactions.every((t: any) => t.selected);
  }

  hasSelection(): boolean {
    return this.transactions.some((t: any) => t.selected);
  }

  deleteSelected = async () => {
    const selectedTx = this.transactions.filter((t: any) => t.selected);
    const selectedIds = selectedTx.map(t => t._id);
    if (selectedIds.length === 0) return;

    const deletedSelected = selectedTx.filter(t => t.deletedAt);
    const activeSelected = selectedTx.filter(t => !t.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Transactions' : 'Delete Transactions';
    const bodyHeader = 'Are you sure?';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted transaction(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted transaction(s) will also be deleted.` : ''}`
      : `You are about to delete ${activeSelected.length} selected transaction(s). This action can be undone later if "Include Deleted" is checked.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    const confirmed = await this.confirmService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      yesBtnCss,
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.svc.deleteTransactions(selectedIds).subscribe({
        next: (res) => {
          this.toastr.success(res?.message || 'Bulk action completed successfully');
          this.allSelected = false;
          this.loadTransactions();
        },
        error: (err) => {
          this.toastr.error(err?.msg || err?.message || 'Failed to process bulk action');
        }
      });
    }
  }

  // ── Loading flags ─────────────────────────────────
  loadingStats        = true;
  loadingChart        = true;
  loadingTransactions = true;

  // ── Filter panel ──────────────────────────────────
  showFilters = false;

  filters: ITransactionFilters = { search: '', startDate: '', endDate: '' };
  // Draft copy edited in the panel; applied only on "Apply"
  draftFilters: ITransactionFilters = { search: '', startDate: '', endDate: '' };
  rangeDates: Date[] | undefined;

  onSearch(value: string | null) {
    this.page = 1;
    this.filters.search = value || '';
    this.loadTransactions();
  }

  onDateFilter() {
    if (!this.rangeDates?.[0] || !this.rangeDates?.[1]) return;
    this.page = 1;
    this.loadTransactions();
  }

  formatLocalDate = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;

  get hasActiveFilters(): boolean {
    return !!(this.filters.search || this.filters.startDate || this.filters.endDate);
  }

  includeDeleted = false;

  toggleIncludeDeleted(): void {
    this.includeDeleted = !this.includeDeleted;
    this.loadTransactions();
  }

  constructor(
    private svc: PaymentTransactionsService,
    private confirmService: ConfirmService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.setupChartOptions();
    this.loadStats();
    this.loadRevenue();
    this.loadTransactions();
  }

  // ── Loaders ───────────────────────────────────────
  private loadStats(): void {
    this.loadingStats = true;
    this.svc.getStats().subscribe({
      next:  (d) => { this.statCards = this.buildStatCards(d); this.loadingStats = false; },
      error: ()  => { this.statCards = this.fallbackStatCards(); this.loadingStats = false; }
    });
  }

  private loadRevenue(): void {
    this.loadingChart = true;
    this.svc.getRevenueOverview(this.activePeriod.value).subscribe({
      next: (d) => {
        this.revenueChartData = {
          labels: d.data.map(p => p.label),
          datasets: [{
            data:                 d.data.map(p => p.revenue),
            borderColor:          '#9ca3af',
            backgroundColor:      'rgba(156,163,175,0.08)',
            fill:                 true,
            tension:              0.4,
            pointRadius:          d.data.map(p => p.revenue > 0 ? 5 : 0),
            pointHoverRadius:     6,
            pointBackgroundColor: '#374151',
            pointBorderColor:     '#fff',
            pointBorderWidth:     2,
            borderWidth:          2
          }]
        };
        this.loadingChart = false;
      },
      error: () => { this.loadingChart = false; }
    });
  }


loadTransactions(): void {
  this.loadingTransactions = true;

  const startDate = this.rangeDates?.[0]
    ? this.formatLocalDate(this.rangeDates[0]) + 'T00:00:00.000Z'
    : undefined;
  const endDate = this.rangeDates?.[1]
    ? this.formatLocalDate(this.rangeDates[1]) + 'T23:59:59.999Z'
    : undefined;

  this.svc.getTransactions({
    page:      this.page,
    limit:     this.limit,
    search:    this.filters.search || undefined,
    startDate,
    endDate,
    includeDeleted: this.includeDeleted
  }).subscribe({
    next: (d) => {
      this.transactions        = d.transactions || [];
      this.originalTransactions = [...this.transactions];
      this.totalCount          = d.totalCount || 0;
      this.hasMore             = d.hasMore || false;
      this.loadingTransactions = false;
      this.allSelected         = false;
      if (this.currentSortField && this.currentSortOrder) {
        this.sortTransactionsLocal();
      }
    },
    error: () => { 
      this.loadingTransactions = false; 
      this.transactions = [];
      this.originalTransactions = [];
    }
  });
}

getInitials(userInfo: ITransactionUserInfo): string {
  const f = userInfo?.firstName?.[0] ?? '';
  const l = userInfo?.lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '??';
}

getDisplayName(userInfo: ITransactionUserInfo): string {
  const name = `${userInfo?.firstName ?? ''} ${userInfo?.lastName ?? ''}`.trim();
  return name || userInfo?.email?.split('@')[0] || 'Unknown';
}

getAvatarBg(index: number): string {
  return AVATAR_PALETTES[index % AVATAR_PALETTES.length].bg;
}

getAvatarColor(index: number): string {
  return AVATAR_PALETTES[index % AVATAR_PALETTES.length].color;
}

// track failed avatar loads
private failedAvatars = new Set<string>();

getProfileImage(userInfo: ITransactionUserInfo, id: string): string | null {
  if (this.failedAvatars.has(id)) return null;
  return userInfo?.profilePhoto ?? null;
}

handleAvatarError(id: string): void {
  this.failedAvatars.add(id);
}

resolvePaymentType(t: ITransaction): { label: string; type: 'card' | 'paypal' | 'bank' } {
  const desc = (t.description ?? '').toLowerCase();
  if (t.type === 'subscription' || desc.includes('subscription'))
    return { label: 'Credit Card', type: 'card' };
  if (desc.includes('stripe') || desc.includes('card'))
    return { label: 'Credit Card', type: 'card' };
  if (desc.includes('paypal'))
    return { label: 'PayPal', type: 'paypal' };
  if (desc.includes('bank') || desc.includes('transfer'))
    return { label: 'Bank Transfer', type: 'bank' };
  return { label: 'Wallet Top-up', type: 'card' };
}



  // ── Filter actions ────────────────────────────────
  openFilters(): void {
    // Copy applied filters into draft so panel shows current state
    this.draftFilters = { ...this.filters };
    this.showFilters  = true;
  }

  applyFilters(): void {
    // Swap dates if user entered them in wrong order
    if (this.draftFilters.startDate && this.draftFilters.endDate &&
        this.draftFilters.startDate > this.draftFilters.endDate) {
      const tmp = this.draftFilters.startDate;
      this.draftFilters.startDate = this.draftFilters.endDate;
      this.draftFilters.endDate   = tmp;
    }
    this.filters     = { ...this.draftFilters };
    this.page        = 1;
    this.showFilters = false;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.draftFilters = { search: '', startDate: '', endDate: '' };
    this.filters      = { search: '', startDate: '', endDate: '' };
    this.rangeDates = undefined;
    this.page         = 1;
    this.showFilters  = false;
    this.loadTransactions();
  }

  closeFilters(): void {
    this.showFilters = false;
  }

  // ── Mapping ───────────────────────────────────────
  private buildStatCards(d: ITransactionStats): IStatCard[] {
    const growth = (g?: string) => g ?? '0%';
    const pos    = (g?: string) => !String(g).startsWith('-');

    return [
      {
        label: 'Total Revenue', value: d.totalRevenue.formatted,
        numericValue: d.totalRevenue.value,
        change: `${growth(d.totalRevenue.growth)} from last month`,
        positive: pos(d.totalRevenue.growth),
        icon: 'pi-dollar', gradient: true,
        iconBg: 'rgba(255,255,255,0.25)', iconColor: '#fff'
      },
      {
        label: 'This Month', value: d.thisMonthRevenue.formatted,
        numericValue: d.thisMonthRevenue.value,
        change: `${growth(d.thisMonthRevenue.growth)} from last month`,
        positive: pos(d.thisMonthRevenue.growth),
        icon: 'pi-calendar', gradient: false,
        iconBg: '#fee2e2', iconColor: '#ef4444'
      },
      {
        label: 'Transactions', value: d.totalTransactions.value.toLocaleString(),
        numericValue: d.totalTransactions.value,
        change: `${growth(d.totalTransactions.growth)} from last month`,
        positive: pos(d.totalTransactions.growth),
        icon: 'pi-receipt', gradient: false,
        iconBg: '#ede9fe', iconColor: '#7c3aed'
      },
      {
        label: 'Success Rate', value: d.successRate.formatted,
        numericValue: d.successRate.value,
        change: `${growth(d.successRate.growth)} from last month`,
        positive: pos(d.successRate.growth),
        icon: 'pi-chart-line', gradient: false,
        iconBg: '#fce7f3', iconColor: '#ec4899'
      },
    ];
  }

  private fallbackStatCards(): IStatCard[] {
    return [
      { label: 'Total Revenue', value: '$0.00', numericValue: 0, change: 'No data', positive: true, icon: 'pi-dollar',      gradient: true,  iconBg: 'rgba(255,255,255,0.25)', iconColor: '#fff'    },
      { label: 'This Month',    value: '$0.00', numericValue: 0, change: 'No data', positive: true, icon: 'pi-calendar',    gradient: false, iconBg: '#fee2e2',                iconColor: '#ef4444' },
      { label: 'Transactions',  value: '0',     numericValue: 0, change: 'No data', positive: true, icon: 'pi-receipt',     gradient: false, iconBg: '#ede9fe',                iconColor: '#7c3aed' },
      { label: 'Success Rate',  value: '0%',    numericValue: 0, change: 'No data', positive: true, icon: 'pi-chart-line',  gradient: false, iconBg: '#fce7f3',                iconColor: '#ec4899' },
    ];
  }





  // ── Chart Setup ───────────────────────────────────
  private setupChartOptions(): void {
    this.revenueChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1f2937',
          padding: 10,
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (ctx: any) => '$' + ctx.parsed.y.toFixed(2)
          }
        }
      },
      scales: {
        x: {
          grid:   { display: false },
          ticks:  { color: '#9ca3af', font: { size: 11 } },
          border: { color: '#e5e7eb' }
        },
        y: {
          grid:  { color: '#f3f4f6' },
          ticks: {
            color: '#9ca3af', font: { size: 11 },
            callback: (v: any) => v >= 1000 ? (v / 1000) + 'k' : `$${v}`
          },
          border: { display: false },
          min: 0
        }
      }
    };
  }

  // ── UI Interactions ───────────────────────────────
  scrollCards(direction: number): void {
    this.statsScroll.nativeElement.scrollBy({ left: direction * 260, behavior: 'smooth' });
  }

  setPeriod(period: typeof this.periods[0]): void {
    this.activePeriod = period;
    this.loadRevenue();
  }

  prevPage(): void {
    if (this.page > 1) { this.page--; this.loadTransactions(); }
  }

  nextPage(): void {
    if (this.hasMore) { this.page++; this.loadTransactions(); }
  }

  onSort(field: string): void {
    if (this.currentSortField === field) {
      if (this.currentSortOrder === "asc") {
        this.currentSortOrder = "desc";
      } else if (this.currentSortOrder === "desc") {
        this.currentSortOrder = null;
        this.currentSortField = null;
      }
    } else {
      this.currentSortField = field;
      this.currentSortOrder = "asc";
    }
    this.sortTransactionsLocal();
  }

  sortTransactionsLocal(): void {
    if (!this.currentSortField || !this.currentSortOrder) {
      this.transactions = [...this.originalTransactions];
      return;
    }

    const direction = this.currentSortOrder === "asc" ? 1 : -1;
    const field = this.currentSortField;

    this.transactions.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'user') {
        valA = this.getDisplayName(a.userInfo).toLowerCase();
        valB = this.getDisplayName(b.userInfo).toLowerCase();
      } else if (field === 'paymentType') {
        valA = this.resolvePaymentType(a).label.toLowerCase();
        valB = this.resolvePaymentType(b).label.toLowerCase();
      } else if (field === 'amount') {
        valA = Number(a.amount || 0);
        valB = Number(b.amount || 0);
      } else if (field === 'date') {
        valA = new Date(a.createdAt).getTime();
        valB = new Date(b.createdAt).getTime();
      } else if (field === 'status') {
        valA = (a.status || '').toLowerCase();
        valB = (b.status || '').toLowerCase();
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });
  }

  getSortIcon(field: string): string {
    if (this.currentSortField !== field) return "⇅";
    if (this.currentSortOrder === "asc") return "↑";
    if (this.currentSortOrder === "desc") return "↓";
    return "⇅";
  }

  get showingStart(): number { return (this.page - 1) * this.limit + 1; }
  get showingEnd():   number { return Math.min(this.page * this.limit, this.totalCount); }

  onTransactionRefunded(): void {
    this.loadStats();
    this.loadRevenue();
    this.loadTransactions();
  }
}